import { parseNumberInput } from "@/lib/parse-number";

/**
 * A typed measurement, which in science is often in E-notation or scientific notation:
 * `6.674e-11`, `5.97E24`, `3.0 × 10^8`, `3.0x10^-5`. Anything else is read exactly as
 * `parseNumberInput` reads it, decimal comma included.
 */

const E_NOTATION = /^(.+?)[eE]([+-]?\d+)$/;
const TIMES_TEN = /^(.+?)\s*[x×*·]\s*10\s*\^\s*\(?([+-]?\d+)\)?$/i;
const UNICODE_MINUS = /−/g;
const MAX_EXPONENT = 300;

export function parseMeasuredNumber(raw: string): number | null {
  const text = raw.trim().replace(UNICODE_MINUS, "-");
  const match = E_NOTATION.exec(text) ?? TIMES_TEN.exec(text);
  if (!match) {
    return parseNumberInput(text);
  }
  const mantissa = parseNumberInput(match[1]);
  const exponent = Number.parseInt(match[2], 10);
  if (mantissa === null || Math.abs(exponent) > MAX_EXPONENT) {
    return null;
  }
  // Built from the string, not mantissa × 10 ** exponent: 6.674e-11 must stay exact
  // to the last digit a double can hold, and multiplying by a power of ten does not.
  const value = String(mantissa).includes("e")
    ? mantissa * 10 ** exponent
    : Number(`${mantissa}e${exponent}`);
  return Number.isFinite(value) ? value : null;
}
